import React, { Fragment, useEffect, useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { VipAndLatestAdsSkeletonLoader } from './VipAndLatestAdsSkeletonLoader';
import { AdsChunkSize } from './AdsWithInfiniteScroll.types';
import { AdsSection } from '@/components/AdsSection/AdsSection';
import { AdOrder, GetAdsResponse, ServiceName } from '@/api/types/ads.types';
import { GET_ADS } from '@/api/queries/ads.queries';
import { InfiniteLoader } from '@/components/InfiniteLoader/InfiniteLoader';
import { EmptySearchResults } from '@/components/EmptySearchResults/EmptySearchResults';
import { useParseQuery } from '@/hooks/useParseQuery/useParseQuery';
import { useBanner } from '@/hooks/useBanner';
import { useCurrentCategory } from '@/hooks/useCurrentCategory/useCurrentCategory';
import { chunkArray, mergeAdsResults } from '@/utils/helpers';
import { usePreviousValue } from '@/hooks/usePreviousValue';
import { ADS_PADDING_WITHOUT_TITLE } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.constants';
import { usePopState } from '@/hooks/usePopState';
import { STORAGE_KEYS } from '@/constants/storageKeys';
import { useSaveAdsHandlersStore } from '@/store/useSaveAdsHandlersStore';
import { ScrollSaver } from '@/components/ScrollSaver/ScrollSaver';
import { getSavedAdsData } from '@/components/ScrollSaver/ScrollSaver.helpers';
import { PAGE_WITH_ADS, QUERY } from '@/constants/common';
import { useQueryParam } from '@/hooks/useQueryParam';
import { normalizeAdsChunk } from '@/components/AdsWithInfiniteScroll/VipAndLatestAdsWithInfiniteScroll.helpers';
import { usePlatformSource } from '@/hooks/usePlatformSource';
import { SearchResultsPageTestIds } from '@/__e2e__/testIds/search-results-page';

export const VipAndLatestAdsWithInfiniteScroll = () => {
  const { t } = useTranslation();
  const { inListBanner } = useBanner();
  const { isAllCategories, currentCategory } = useCurrentCategory();
  const { isPopState, resetPopState } = usePopState();
  const setSaveLastAdsHandler = useSaveAdsHandlersStore(
    state => state.setSaveLastAdsHandler
  );
  const { latestAdsData: savedLatestAdsData, vipAdsData: savedVipAdsData } =
    getSavedAdsData(PAGE_WITH_ADS.SEARCH_RESULTS) || {};
  const { keywords, price, regionId, order, propertyOptions, userLegacyId } =
    useParseQuery();
  const source = usePlatformSource();

  const categoryId = currentCategory?.id;
  const filters = {
    categoryId: isAllCategories ? null : categoryId,
    price,
    regionId,
    propertyOptions,
    userLegacyId,
  };
  const [keywordsSource] = useQueryParam(QUERY.keywords_source);
  const currentUrl = window.location.href;

  const {
    data: vipAdsData,
    loading: vipAdsLoading,
    refetch: refetchVipAds,
    fetchMore: fetchMoreVip,
    updateQuery: updateVipAdsQuery,
  } = useQuery<GetAdsResponse>(GET_ADS(ServiceName.Vip), {
    variables: {
      orderType: AdOrder.Random,
      adKind: ServiceName.Vip,
      first: AdsChunkSize.Small,
      keywords,
      filters,
      source,
    },
    onCompleted: data =>
      normalizeAdsChunk(data, AdsChunkSize.Small, updateVipAdsQuery),
  });

  const {
    data: latestAdsData,
    loading: latestAdsLoading,
    refetch: refetchLatestAds,
    fetchMore: fetchMoreLatest,
    updateQuery: updateLatestAdsQuery,
  } = useQuery<GetAdsResponse>(GET_ADS(ServiceName.Latest), {
    variables: {
      orderType: order,
      first: AdsChunkSize.Large,
      keywords,
      filters,
      keywordsSource: keywordsSource?.toUpperCase(),
      sourceLink: currentUrl,
      source,
    },
    onCompleted: data =>
      normalizeAdsChunk(data, AdsChunkSize.Large, updateLatestAdsQuery),
  });

  const resetKey = JSON.stringify({ ...filters, keywords, order });

  const prevResetKey = usePreviousValue(resetKey);

  useEffect(() => {
    if (
      prevResetKey && // to avoid resetting on the first render
      prevResetKey !== resetKey &&
      !isPopState
    ) {
      refetchVipAds().then(({ data }) =>
        normalizeAdsChunk(data, AdsChunkSize.Small, updateVipAdsQuery)
      );
      refetchLatestAds().then(({ data }) =>
        normalizeAdsChunk(data, AdsChunkSize.Large, updateLatestAdsQuery)
      );
    }
    resetPopState();
  }, [resetKey]);

  useLayoutEffect(() => {
    if (!latestAdsData && savedLatestAdsData) {
      updateLatestAdsQuery(() => savedLatestAdsData);
    }
    if (!vipAdsData && savedVipAdsData) {
      updateVipAdsQuery(() => savedVipAdsData);
    }
  }, []);

  useEffect(() => {
    setSaveLastAdsHandler(() => {
      const data = JSON.stringify({
        page: PAGE_WITH_ADS.SEARCH_RESULTS,
        vipAdsData,
        latestAdsData,
      });
      localStorage.setItem(STORAGE_KEYS.LAST_ADS_DATA, data);
    });
  }, [latestAdsData, vipAdsData]);

  const { endCursor, hasNextPage } = latestAdsData?.ads?.pageInfo || {};
  const isEmptyResults = !latestAdsData?.ads?.nodes?.some(Boolean);

  const { chunkedVipAds, chunkedLatestAds } = useMemo(() => {
    return {
      chunkedVipAds: chunkArray(
        vipAdsData?.ads?.nodes || [],
        AdsChunkSize.Small
      ),
      chunkedLatestAds: chunkArray(
        latestAdsData?.ads?.nodes || [],
        AdsChunkSize.Large
      ),
    };
  }, [vipAdsData, latestAdsData]);

  const fetchMoreVipAds = () =>
    fetchMoreVip({
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeAdsResults(prevResult, fetchMoreResult, {
          chunkSize: AdsChunkSize.Small,
        }),
    });

  const fetchMoreLatestAds = () =>
    fetchMoreLatest({
      variables: {
        after: endCursor,
      },
      updateQuery: (prevResult, { fetchMoreResult }) =>
        mergeAdsResults(prevResult, fetchMoreResult, {
          chunkSize: AdsChunkSize.Large,
        }),
    });

  async function onLoadMore() {
    if (
      !vipAdsLoading &&
      !latestAdsLoading &&
      hasNextPage &&
      navigator.onLine
    ) {
      await Promise.all([fetchMoreVipAds(), fetchMoreLatestAds()]);
    }
  }

  if (vipAdsLoading || latestAdsLoading) {
    return <VipAndLatestAdsSkeletonLoader />;
  }

  if (isEmptyResults) {
    return <EmptySearchResults />;
  }

  return (
    <>
      <ScrollSaver page={PAGE_WITH_ADS.SEARCH_RESULTS} />

      {chunkedLatestAds.map((latestAdsChunk, i) => {
        const isFirstChunk = !i;
        const isLastChunk = i === chunkedLatestAds.length - 1;
        const vipAds = chunkedVipAds[i]?.filter(Boolean);
        const latestAds = latestAdsChunk?.filter(Boolean);
        const key = `vip_and_latest_${i}`;
        const hasVipAds = vipAds?.length > 0;
        const latestAdsTitle =
          isFirstChunk || hasVipAds ? t('pages.ads.ads') : undefined;
        const latestAdCardsCustomStyles = {
          paddingTop:
            !isFirstChunk && !hasVipAds ? ADS_PADDING_WITHOUT_TITLE : undefined,
          paddingBottom:
            !isLastChunk && !hasVipAds ? ADS_PADDING_WITHOUT_TITLE : undefined,
        };

        return (
          <Fragment key={key}>
            {!!vipAds?.length && (
              <AdsSection
                title={t('pages.ads.vip_ads')}
                ads={vipAds}
                hasTopMargin={isFirstChunk}
                dataTestId={SearchResultsPageTestIds.vipAdsSection}
                hasFirstCardPriority
              />
            )}

            {!!latestAds?.length && (
              <AdsSection
                ads={latestAds}
                title={latestAdsTitle}
                hasAdManagerCard={i === 0}
                bannerInList={inListBanner}
                isBannerInListAtTheEnd
                isBannerInListAfterThreeRows
                adCardsCustomStyles={latestAdCardsCustomStyles}
                dataTestId={SearchResultsPageTestIds.latestAdsSection}
              />
            )}
          </Fragment>
        );
      })}

      <InfiniteLoader onVisit={onLoadMore} />
    </>
  );
};
